import type { EpochRecord, Manifest, SpectralState } from "../data/types";
import { formatOffsetKms, hbetaOffsetKms } from "../data/types";
import type { Mode } from "../domain/mode";

interface Props {
  object: Manifest["object"];
  epoch: EpochRecord;
  mode: Mode;
  reddeningAv: number;
  /** Template being reddened in dust mode; null outside it. */
  reddeningTemplateState: SpectralState | null;
}

function kinematicSentence(epoch: EpochRecord): string | null {
  const offset = hbetaOffsetKms(epoch);
  if (offset === null) return null;
  const direction = offset < 0 ? "blueward" : "redward";
  const fwhm = Math.round(epoch.measurements.Hbeta.fwhm_kms).toLocaleString();
  return `In this ${epoch.date} epoch the broad Hβ peak sits ${formatOffsetKms(offset)} ${direction} of the systemic velocity, with FWHM ${fwhm} km/s.`;
}

export function NarrativePanel(props: Props) {
  const { object, epoch, mode, reddeningAv, reddeningTemplateState } = props;
  const stateText = epoch.state ? object.state_narrative?.[epoch.state] ?? null : null;
  const kinematic = kinematicSentence(epoch);

  return (
    <div className="narrative-panel panel">
      <h2>What you are seeing</h2>
      {stateText ? (
        <p className="narrative-text">{stateText}</p>
      ) : (
        <>
          {object.narrative ? <p className="narrative-text">{object.narrative}</p> : null}
          {kinematic ? (
            <p className="narrative-text" style={{ marginTop: 8 }}>
              {kinematic}
            </p>
          ) : null}
        </>
      )}

      {mode === "interpolated" ? (
        <p className="narrative-text" style={{ marginTop: 8 }}>
          <strong>Interpolated mode.</strong> Between observed epochs the spectrum and audio
          are crossfaded. This visualises continuity but is not a measurement at intermediate
          dates.
        </p>
      ) : null}

      {mode === "reddening" ? (
        <p className="narrative-text" style={{ marginTop: 8 }}>
          <strong>Dust / reddening mode.</strong> A brighter template (
          {reddeningTemplateState ?? "Bright"}) is reddened by A<sub>V</sub>={" "}
          {reddeningAv.toFixed(2)} using the selected extinction law, then compared with
          the dim epoch. Where a single extinction law reproduces the dim spectrum, that
          favours variable line-of-sight obscuration; where it fails, an intrinsic change
          in the accretion flow is implied.
          {object.reddening_note ? ` ${object.reddening_note}` : ""}
        </p>
      ) : null}

      {mode === "all_epochs" ? (
        <p className="narrative-text" style={{ marginTop: 8 }}>
          <strong>All-epochs mode.</strong> Every observed spectrum plays in parallel
          {object.all_epochs_note ? `, ${object.all_epochs_note}` : ""}. The faders below the plot let
          you re-balance the mix or solo a single epoch — these are listening aids, not a
          physical decomposition of the source.
        </p>
      ) : null}

      <p className="narrative-text" style={{ marginTop: 8, fontSize: 12, color: "var(--text-dim)" }}>
        Reference: {object.paper}.
      </p>
    </div>
  );
}
